import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { INotification } from "Shared/Types/notifications";
import {
  checkNotificationsCount,
  checkUrgentNotifications,
  getNotifications,
  getNotificationsMore,
} from "./reducer";

interface INotificationTypeState {
  hasUrgentNotifications: boolean;
  notificationsCount: number;
  loading: boolean | null;
  notifications: {
    next: null | string;
    count: number;
    results: INotification[];
  };
  error?: null | string;
}

const initialState: INotificationTypeState = {
  hasUrgentNotifications: false,
  notificationsCount: 0,
  loading: null,
  notifications: {
    count: 0,
    next: null,
    results: [],
  },
  error: null,
};

const notificationSlice = createSlice({
  name: "notificationSlice",
  initialState,
  reducers: {
    hideAllNotificationsState: (state) => {
      state.notificationsCount = 0;
      state.hasUrgentNotifications = false;
      state.notifications.results = state.notifications.results.map((item) => ({
        ...item,
        is_read: true,
      }));
      state.notifications.next = null;
    },
    deleteAllNotificationsState: (state) => {
      state.notificationsCount = 0;
      state.hasUrgentNotifications = false;
      state.notifications = { ...initialState.notifications };
    },
    decreaseNotificationCount: (state) => {
      if (state.notificationsCount > 0) state.notificationsCount--;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      state.error = null;
      state.notifications = {
        results: action.payload.results,
        next: action.payload.next,
        count: action.payload.count,
      };
      state.loading = false;
    });
    builder.addCase(getNotifications.pending, (state) => {
      state.error = null;
      state.loading = true;
    });
    builder.addCase(getNotifications.rejected, (state, error) => {
      state.loading = false;
      state.error = error.error.message;
    });

    builder.addCase(getNotificationsMore.fulfilled, (state, action) => {
      state.error = null;
      state.notifications = {
        results: [...state.notifications.results, ...action.payload.results],
        next: action.payload.next,
        count: action.payload.count,
      };
      state.loading = false;
      state.notificationsCount = action.payload.count;
    });
    builder.addCase(getNotificationsMore.pending, (state) => {
      state.error = null;
      state.loading = true;
    });
    builder.addCase(getNotificationsMore.rejected, (state, error) => {
      state.loading = false;
      state.error = error.error.message;
    });

    builder.addCase(checkUrgentNotifications.fulfilled, (state, action) => {
      state.hasUrgentNotifications = !!action.payload.count;
    });
    builder.addCase(checkNotificationsCount.fulfilled, (state, action) => {
      state.notificationsCount = action.payload.count;
    });
  },
});

export const {
  hideAllNotificationsState,
  decreaseNotificationCount,
  deleteAllNotificationsState,
} = notificationSlice.actions;
export const selectNotificationsCount = (state: RootState) =>
  state.notificationSlice.notificationsCount;
export const selectUrgentNotificationsCount = (state: RootState) =>
  state.notificationSlice.hasUrgentNotifications;
export const selectNotifications = (state: RootState) =>
  state.notificationSlice.notifications;
export const selectNotificationsState = (state: RootState) =>
  state.notificationSlice;
export const selectNotificationLoading = (state: RootState) =>
  state.notificationSlice.loading;
export default notificationSlice;
